import { config } from '../config/environment';
import { loadStripe, Stripe } from '@stripe/stripe-js';

const BASE = `${config.apiBaseUrl}/payments`;

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// ── Types ──────────────────────────────────────────────────────────────────────

export interface ConnectStatus {
  stripe_account_id: string | null;
  onboarding_complete: boolean;
  details_submitted: boolean;
  charges_enabled: boolean;
  payouts_enabled: boolean;
}

export interface PaymentSettings {
  session_rate_cents: number | null;
  currency: string;
  stripe_account_id: string | null;
  onboarding_complete: boolean;
  details_submitted: boolean;
}

export interface AppointmentPaymentStatus {
  appointment_id: string;
  status: 'no_rate' | 'unpaid' | 'pending' | 'processing' | 'succeeded' | 'failed' | 'refunded' | 'manual' | 'waived';
  pricing_rate_cents?: number | null;
  pricing_currency?: string | null;
  payment: {
    id: string;
    amount_cents: number;
    platform_fee_cents: number;
    currency: string;
    stripe_payment_intent_id: string | null;
    paid_at: string | null;
    payment_method?: string | null;
    payment_method_note?: string | null;
  } | null;
}

export type ManualPaymentMethod = 'cash' | 'venmo' | 'paypal' | 'square' | 'stripe' | 'other';

export const MANUAL_PAYMENT_METHODS: { value: ManualPaymentMethod; label: string }[] = [
  { value: 'cash',    label: 'Cash' },
  { value: 'venmo',   label: 'Venmo' },
  { value: 'paypal',  label: 'PayPal' },
  { value: 'square',  label: 'Square' },
  { value: 'stripe',  label: 'Stripe' },
  { value: 'other',   label: 'Other…' },
];

export async function updateMeetingStatus(
  appointmentId: string,
  status: 'scheduled' | 'completed' | 'no_show' | 'cancelled',
): Promise<void> {
  const token = localStorage.getItem('authToken');
  const r = await fetch(`${config.apiBaseUrl}/session/${appointmentId}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ status }),
  });
  if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || 'Failed to update status'); }
}

export async function recordManualPayment(
  appointmentId: string,
  body: { amount_cents: number; payment_method: ManualPaymentMethod; payment_method_note?: string },
): Promise<void> {
  const token = localStorage.getItem('authToken');
  const r = await fetch(`${config.apiBaseUrl}/session/${appointmentId}/payment/manual`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || 'Failed to record payment'); }
}

export async function resetManualPayment(appointmentId: string): Promise<void> {
  const token = localStorage.getItem('authToken');
  const r = await fetch(`${config.apiBaseUrl}/session/${appointmentId}/payment/manual`, {
    method: 'DELETE',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || 'Failed to reset payment'); }
}

export async function updateSessionDetails(
  appointmentId: string,
  data: { appointment_type?: string; barter_note?: string },
): Promise<void> {
  const token = localStorage.getItem('authToken');
  const r = await fetch(`${config.apiBaseUrl}/session/${appointmentId}/details`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(data),
  });
  if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || 'Failed to update session details'); }
}

export interface PaymentIntentResult {
  client_secret: string;
  payment_intent_id: string;
  customer_id: string;
  amount_cents: number;
  currency: string;
  stripe_account_id: string;
  /** Saved Stripe PaymentMethod ID from a prior session, or null if no card on file. */
  existing_payment_method_id: string | null;
  /** Last 4 digits of the saved card, or null if no card on file. */
  card_last4: string | null;
}

// ── Stripe.js singleton ────────────────────────────────────────────────────────

let stripePromise: Promise<Stripe | null> | null = null;

export function getStripe(): Promise<Stripe | null> {
  if (!stripePromise) {
    const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
    stripePromise = key ? loadStripe(key) : Promise.resolve(null);
  }
  return stripePromise;
}

/**
 * Load Stripe scoped to a connected account.
 * PaymentIntents created with stripe_account= must be confirmed using a
 * Stripe.js instance initialized with the same stripeAccount option.
 */
export function getStripeForAccount(stripeAccountId: string): Promise<Stripe | null> {
  const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
  return key ? loadStripe(key, { stripeAccount: stripeAccountId }) : Promise.resolve(null);
}

// ── Coach: Connect onboarding ──────────────────────────────────────────────────

export async function getConnectStatus(): Promise<ConnectStatus> {
  const res = await fetch(`${BASE}/connect/status`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to get Stripe Connect status');
  return res.json();
}

export async function startOnboarding(): Promise<string> {
  const res = await fetch(`${BASE}/connect/onboard`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any).detail || 'Failed to start Stripe onboarding');
  }
  const data = await res.json();
  return data.onboarding_url as string;
}

export async function getDashboardLink(): Promise<string> {
  const res = await fetch(`${BASE}/connect/dashboard-link`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to get Stripe dashboard link');
  const data = await res.json();
  return data.url as string;
}

export async function disconnectStripe(): Promise<void> {
  const res = await fetch(`${BASE}/connect/disconnect`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any).detail || 'Failed to disconnect Stripe');
  }
}

// ── Coach: Payment settings ────────────────────────────────────────────────────

export async function getPaymentSettings(): Promise<PaymentSettings> {
  const res = await fetch(`${BASE}/settings`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to get payment settings');
  return res.json();
}

export async function updatePaymentSettings(
  sessionRateCents: number,
  currency: string = 'usd',
): Promise<void> {
  const res = await fetch(`${BASE}/settings`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ session_rate_cents: sessionRateCents, currency }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any).detail || 'Failed to update payment settings');
  }
}

// ── Per-session pricing ────────────────────────────────────────────────────────

export interface AppointmentPricing {
  rate: number;
  currency: string;
  notes: string | null;
  status: 'unpaid' | 'pending' | 'paid' | 'refunded' | 'waived';
  set_by: string;
}

export async function setAppointmentPricing(
  appointmentId: string,
  rate: number,
  currency: string = 'usd',
  notes?: string,
): Promise<void> {
  const res = await fetch(`${BASE}/pricing/${appointmentId}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ rate, currency, notes }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any).detail || 'Failed to set pricing');
  }
}

export async function getAppointmentPricing(
  appointmentId: string,
): Promise<{ appointment_id: string; pricing: AppointmentPricing | null }> {
  const res = await fetch(`${BASE}/pricing/${appointmentId}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to get pricing');
  return res.json();
}

export async function updatePricingStatus(
  appointmentId: string,
  status: AppointmentPricing['status'],
): Promise<void> {
  const res = await fetch(`${BASE}/pricing/${appointmentId}/status`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error('Failed to update pricing status');
}

// ── Client: Pay for session ────────────────────────────────────────────────────

export async function createPaymentIntent(appointmentId: string): Promise<PaymentIntentResult> {
  const res = await fetch(`${BASE}/session/${appointmentId}/pay`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any).detail || 'Failed to create payment');
  }
  return res.json();
}

export async function chargeClient(appointmentId: string): Promise<{
  payment_intent_id: string;
  amount_cents: number;
  currency: string;
  status: string;
}> {
  const res = await fetch(`${BASE}/session/${appointmentId}/charge`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any).detail || 'Failed to charge client');
  }
  return res.json();
}

export async function getAppointmentPaymentStatus(
  appointmentId: string,
  checkStripe: boolean = false,
): Promise<AppointmentPaymentStatus> {
  const url = checkStripe
    ? `${BASE}/appointment/${appointmentId}/status?check_stripe=true`
    : `${BASE}/appointment/${appointmentId}/status`;
  const res = await fetch(url, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to get payment status');
  return res.json();
}

export async function getAppointmentPaymentStatusBatch(
  appointmentIds: string[],
): Promise<Record<string, AppointmentPaymentStatus>> {
  if (appointmentIds.length === 0) return {};
  const res = await fetch(`${BASE}/appointments/statuses?appointment_ids=${appointmentIds.join(',')}`, {
    headers: authHeaders(),
  });
  if (!res.ok) return {};
  const data = await res.json();
  return data.statuses ?? {};
}

export async function postPaymentSave(
  appointmentId: string,
  paymentMethodId: string,
  customerId: string,
): Promise<void> {
  await fetch(`${BASE}/session/${appointmentId}/post-payment-save`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ payment_method_id: paymentMethodId, customer_id: customerId }),
  });
  // non-fatal — don't throw on failure
}

export async function createPackagePaymentIntent(packagePaymentId: string): Promise<PaymentIntentResult> {
  const res = await fetch(`${BASE}/packages/payments/${packagePaymentId}/pay`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any).detail || 'Failed to create payment');
  }
  return res.json();
}

export async function postPackagePaymentSave(
  packagePaymentId: string,
  paymentMethodId: string,
  customerId: string,
  paymentIntentId?: string,
): Promise<void> {
  await fetch(`${BASE}/packages/payments/${packagePaymentId}/post-payment-save`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ payment_method_id: paymentMethodId, customer_id: customerId, payment_intent_id: paymentIntentId ?? null }),
  });
}

// ── Coach: saved card lookup ───────────────────────────────────────────────────

export async function getClientPaymentMethod(
  clientId: string,
): Promise<{ has_card: boolean; card_last4?: string; card_brand?: string; payment_method_id?: string }> {
  const res = await fetch(`${BASE}/client/${clientId}/payment-method`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to get payment method');
  return res.json();
}

// ── Helpers ────────────────────────────────────────────────────────────────────

export interface CashflowSession {
  appointment_id: string;
  meeting_name: string;
  scheduled_at: string;
  duration_minutes: number;
  appointment_status: string;
  client_id: string;
  client_name: string;
  client_email: string;
  rate_cents: number | null;
  currency: string;
  payment_status: 'no_rate' | 'unpaid' | 'pending' | 'succeeded' | 'failed' | 'refunded' | 'waived' | 'manual';
  amount_collected_cents: number | null;
  platform_fee_cents: number | null;
  stripe_fee_cents: number | null;
  net_cents: number | null;
  stripe_payment_intent_id: string | null;
  stripe_charge_id: string | null;
  failure_reason: string | null;
  refunded_amount_cents: number | null;
  refund_reason: string | null;
  stripe_refund_id: string | null;
  refunded_at: string | null;
  paid_at: string | null;
  payment_method: string | null;
  pricing_notes: string | null;
}

export interface CashflowReport {
  summary: {
    total_sessions: number;
    total_collected_cents: number;
    total_outstanding_cents: number;
  };
  sessions: CashflowSession[];
}

export async function getCashflowReport(params: {
  date_from?: string;
  date_to?: string;
  client_id?: string;
  status?: string;
} = {}): Promise<CashflowReport> {
  const qs = new URLSearchParams();
  if (params.date_from) qs.set('date_from', params.date_from);
  if (params.date_to) qs.set('date_to', params.date_to);
  if (params.client_id) qs.set('client_id', params.client_id);
  if (params.status) qs.set('status', params.status);
  const res = await fetch(`${BASE}/report/cashflow?${qs.toString()}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to load cashflow report');
  return res.json();
}

export async function recordPaymentFailure(appointmentId: string, reason: string): Promise<void> {
  const res = await fetch(`${BASE}/session/${appointmentId}/record-failure`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error((d as { detail?: string }).detail || 'Failed to record payment failure');
  }
}

export async function refundSessionPayment(
  appointmentId: string,
  reason: string,
  channel: 'stripe' | 'manual',
): Promise<void> {
  const res = await fetch(`${BASE}/session/${appointmentId}/refund`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ reason, channel }),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error((d as { detail?: string }).detail || 'Refund failed');
  }
}

export interface PackagePayment {
  payment_id: string;
  enrollment_id: string;
  package_id: string;
  package_name: string;
  payment_type: 'setup_fee' | 'installment' | 'pay_in_full';
  installment_number: number | null;
  client_id: string;
  client_name: string;
  client_email: string;
  amount_cents: number;
  currency: string;
  // 'manual' = manually recorded; 'succeeded' = Stripe; 'paid' = legacy manual; all normalize to 'paid' in cashflow
  payment_status: 'pending' | 'manual' | 'paid' | 'succeeded' | 'failed' | 'refunded';
  due_date: string | null;
  paid_at: string | null;
  failure_reason: string | null;
  stripe_payment_intent_id: string | null;
  stripe_charge_id: string | null;
  stripe_fee_cents: number | null;
  net_cents: number | null;
  payment_method: string | null;
}

export interface PackageCashflowReport {
  summary: {
    total_payments: number;
    total_collected_cents: number;
    total_outstanding_cents: number;
  };
  payments: PackagePayment[];
}

export async function getPackageCashflowReport(params: {
  date_from?: string;
  date_to?: string;
  client_id?: string;
  status?: string;
} = {}): Promise<PackageCashflowReport> {
  const qs = new URLSearchParams();
  if (params.date_from) qs.set('date_from', params.date_from);
  if (params.date_to) qs.set('date_to', params.date_to);
  if (params.client_id) qs.set('client_id', params.client_id);
  if (params.status) qs.set('status', params.status);
  const res = await fetch(`${BASE}/report/cashflow/packages?${qs.toString()}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to load package cashflow report');
  return res.json();
}

export async function markPackagePaymentPaid(enrollmentId: string, paymentId: string, paymentMethod: string): Promise<void> {
  const res = await fetch(
    `${config.apiBaseUrl}/packages/enrollments/${enrollmentId}/payments/${paymentId}/mark-paid`,
    {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ payment_method: paymentMethod }),
    },
  );
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error((d as { detail?: string }).detail || 'Failed to mark payment as paid');
  }
}

export async function chargePackagePayment(enrollmentId: string, paymentId: string): Promise<{ status: string; failure_reason: string | null }> {
  const res = await fetch(
    `${config.apiBaseUrl}/packages/enrollments/${enrollmentId}/payments/${paymentId}/charge`,
    { method: 'POST', headers: authHeaders() },
  );
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error((d as { detail?: string }).detail || 'Stripe charge failed');
  }
  return res.json();
}

export async function createPackageInvoice(enrollmentId: string, paymentId: string): Promise<{ invoice_token: string | null; invoice_id: string | null }> {
  const res = await fetch(
    `${config.apiBaseUrl}/packages/enrollments/${enrollmentId}/payments/${paymentId}/create-invoice`,
    { method: 'POST', headers: authHeaders() },
  );
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error((d as { detail?: string }).detail || 'Failed to create invoice');
  }
  return res.json();
}

export function formatCents(cents: number, currency: string = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

// ── Link-based payment accounts (PayPal, Venmo, Cash App, Zelle) ──────────────

export interface LinkAccount {
  provider: string;   // 'paypal' | 'venmo' | 'cashapp' | 'zelle'
  handle: string;
  is_active: boolean;
  payment_url: string | null;
}

export async function getLinkAccounts(): Promise<LinkAccount[]> {
  const res = await fetch(`${BASE}/link-accounts`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to get link accounts');
  return res.json();
}

export async function upsertLinkAccount(provider: string, handle: string): Promise<LinkAccount> {
  const res = await fetch(`${BASE}/link-accounts/${provider}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ handle }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any).detail || 'Failed to save account');
  }
  return res.json();
}

export async function deleteLinkAccount(provider: string): Promise<void> {
  const res = await fetch(`${BASE}/link-accounts/${provider}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to remove account');
}

/** Called by client dashboard — returns active link accounts for a coach. */
export async function getCoachLinkAccounts(coachId: string): Promise<LinkAccount[]> {
  const token = localStorage.getItem('authToken');
  const res = await fetch(`${BASE}/link-accounts/client/${coachId}`, {
    headers: { Authorization: token ? `Bearer ${token}` : '' },
  });
  if (!res.ok) return [];
  return res.json();
}
